import React, { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { httpsCallable, firebaseFunctions } from '../services/firebaseService';
import { useAuth } from '../contexts/AuthContext';
import { useFocusTrap } from '../hooks/useFocusTrap';

const LANG_LABELS: Record<string, string> = {
    de: 'Deutsch',
    en: 'English',
    fr: 'Français',
    it: 'Italiano',
};

interface LanguageUnavailableModalProps {
    availableLanguages: string[];
    onLanguageSelected: (lang: string) => void;
}

const LanguageUnavailableModal: React.FC<LanguageUnavailableModalProps> = ({
    availableLanguages,
    onLanguageSelected,
}) => {
    const { t, i18n } = useTranslation();
    const { userProfile } = useAuth();
    const [saving, setSaving] = useState(false);
    const [selected, setSelected] = useState<string>('');
    const dialogRef = useRef<HTMLDivElement>(null);
    // Fokus bleibt im Dialog; ohne Escape, weil eine Sprache gewählt werden muss
    useFocusTrap(dialogRef);

    const handleSelect = async (lang: string) => {
        if (!lang || saving) return;
        setSaving(true);

        // Switch language immediately for instant feedback
        await i18n.changeLanguage(lang);

        // Try to persist to the backend — include displayName as it is required by the schema
        try {
            if (firebaseFunctions) {
                const updateFn = httpsCallable(firebaseFunctions, 'updateuserprofile');
                await updateFn({
                    displayName: userProfile?.displayName || userProfile?.email || 'User',
                    language: lang,
                });
            }
        } catch (err) {
            console.error('[LanguageUnavailableModal] Failed to persist language selection:', err);
        } finally {
            setSaving(false);
            onLanguageSelected(lang);
        }
    };

    return (
        /* Blocking modal – no close button / backdrop click */
        <div className="fixed inset-0 bg-gray-900/70 backdrop-blur-sm z-[100] flex items-center justify-center p-4">
            <div ref={dialogRef} className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-8 animate-fade-in" role="dialog" aria-modal="true" aria-labelledby="language-unavailable-title">
                {/* Icon */}
                <div className="w-14 h-14 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-5">
                    <svg className="w-7 h-7 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                            d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                    </svg>
                </div>

                <h2 id="language-unavailable-title" className="text-lg font-bold text-gray-900 text-center mb-3">
                    {t('languageUnavailableModal.title')}
                </h2>
                <p className="text-sm text-gray-600 text-center mb-6 leading-relaxed">
                    {t('languageUnavailableModal.message')}
                </p>

                <p className="text-xs font-bold uppercase text-gray-500 mb-3">
                    {t('languageUnavailableModal.selectPrompt')}
                </p>

                <div className="space-y-2">
                    {availableLanguages.map((lang) => (
                        <button
                            key={lang}
                            type="button"
                            disabled={saving}
                            onClick={() => {
                                setSelected(lang);
                                handleSelect(lang);
                            }}
                            className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl border text-sm font-medium transition-all
                                ${selected === lang && saving
                                    ? 'bg-blue-600 border-blue-600 text-white'
                                    : 'bg-white border-gray-200 text-gray-800 hover:bg-blue-50 hover:border-blue-300'
                                }
                                disabled:opacity-60 disabled:cursor-not-allowed`}
                        >
                            {selected === lang && saving ? (
                                <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                            ) : (
                                <span className="w-4 h-4 rounded-full border-2 border-gray-300 flex-shrink-0" />
                            )}
                            {LANG_LABELS[lang] ?? lang.toUpperCase()}
                        </button>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default LanguageUnavailableModal;
